<!--
	@name app-academy
	@description academy page
	@date 2020/05/20
	@license no license
	@copywrite Answers In Retirement Limited
-->

<template>
	<div :component="$options.name">
		<v-carousel class="hero" hide-delimiters :show-arrows="$vuetify.breakpoint.mdAndUp" cycle continuous>
			<v-carousel-item v-for="(banner, index) in banners" :key="index" :src="banner.image | urlize" cover>
				<v-container fill-height>
					<v-row no-gutters :style="`max-width: ${banner.maxWidth};`">
						<v-col cols="12" sm="12" class="pr-10">
							<div v-if="banner.title" :class="banner.titleClass || 'text-h3 text-lg-h2 white--text font-weight-bold mb-0'" :style="banner.titleStyle">
								{{ banner.title }}
							</div>
							<div v-if="banner.copy" :class="banner.copyClass || 'banner--copy text-h5 text-lg-h4 white--text font-weight-light mt-5'">
								{{ banner.copy }}
							</div>
							<v-btn v-if="banner.cta" :color="banner.cta.color" outlined large class="mt-10" @click="handleCtaClick({ ...banner.cta.click })">
								{{ banner.cta.text }}
							</v-btn>
						</v-col>
					</v-row>
				</v-container>
			</v-carousel-item>
		</v-carousel>

		<v-container class="section0 py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<div class="text-center d-flex align-center flex-column px-md-10">
				<p class="text-h5 text-md-h4 text-lg-h3 font-weight-bold mb-6" :style="{ 'line-height': $vuetify.breakpoint.lgAndUp ? '3.75rem' : 'auto' }">
					Education that Drives Business Growth
				</p>
				<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-4">
					The laterlife lending market is transforming rapidly. With expanding client needs and evolving financial complexities, staying ahead is essential to remain competitive and deliver exceptional advice.
				</p>
				<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-0">
					Air Academy is your trusted partner in professional development, offering industry-leading training to:
				</p>
				<v-container class="my-6">
					<v-row class="mb-0">
						<v-col cols="12" sm="4" class="text-center" style="perspective: 120px;">
							<div class="icon--small background--wavy rounded-lg mb-4">
								<svg xmlns="http://www.w3.org/2000/svg" class="svg" viewBox="0 0 800 800">
									<path class="cls-1" d="M631.85,540.63h-167.45c-11.56,0-20.93,9.38-20.93,20.93s9.38,20.93,20.93,20.93h167.45c19.24,0,34.89,15.64,34.89,34.88v13.95c0,19.24-15.65,34.88-34.89,34.88h-27.91c-11.56,0-20.93,9.38-20.93,20.93s9.37,20.93,20.93,20.93h27.91c42.33,0,76.75-34.42,76.75-76.74v-13.95c0-42.32-34.42-76.74-76.75-76.74ZM520.21,666.21h-96.53l-51.05-153.13c-2.85-8.53-10.85-14.31-19.85-14.31h-223.26c-6.73,0-13.04,3.24-16.98,8.69-3.94,5.45-5,12.48-2.87,18.86l55.82,167.44c2.85,8.53,10.84,14.31,19.85,14.31h334.88c11.56,0,20.93-9.38,20.93-20.93s-9.37-20.93-20.93-20.93ZM200.42,666.21l-41.86-125.58h179.14l41.86,125.58h-179.14ZM548.12,512.72c42.33,0,76.75-34.42,76.75-76.76s-34.42-76.74-76.75-76.74-76.75,34.43-76.75,76.74,34.42,76.76,76.75,76.76ZM548.12,401.08c19.24,0,34.89,15.64,34.89,34.88s-15.65,34.9-34.89,34.9-34.89-15.64-34.89-34.9,15.64-34.88,34.89-34.88Z" />
									<path class="cls-2" d="M407.04,311.94c-22.48-22.48-53.59-22.48-110.08-22.48s-87.61,0-110.1,22.48c-22.46,22.48-22.46,53.59-22.46,110.07v13.95c0,11.57,9.38,20.94,20.93,20.94s20.93-9.38,20.93-20.94v-13.95c0-48.71.41-70.67,10.21-80.48,9.81-9.81,31.77-10.22,80.49-10.22s70.67.41,80.48,10.22c9.81,9.81,10.22,31.76,10.22,80.48v13.95c0,11.57,9.37,20.94,20.93,20.94s20.93-9.38,20.93-20.94v-13.95c0-56.48,0-87.59-22.48-110.07ZM296.97,261.55c42.33,0,76.75-34.42,76.75-76.74s-34.42-76.74-76.75-76.74-76.75,34.42-76.75,76.74,34.42,76.74,76.75,76.74ZM296.97,149.92c19.24,0,34.89,15.64,34.89,34.88s-15.65,34.88-34.89,34.88-34.89-15.65-34.89-34.88,15.64-34.88,34.89-34.88Z" />
								</svg>
							</div>
							<p class="text-h6 mb-0">
								Enhance your expertise
							</p>
						</v-col>
						<v-col cols="12" sm="4" style="perspective: 120px;">
							<div class="icon--small background--wavy background--wavy--alt rounded-lg mb-4">
								<svg xmlns="http://www.w3.org/2000/svg" class="svg" viewBox="0 0 800 800">
									<path class="cls-1" d="M547.54,109.67c-88.54,0-160.59,71.98-160.59,160.45s72.04,160.45,160.59,160.45,160.6-71.98,160.6-160.45-72.06-160.45-160.6-160.45ZM547.54,388.7c-65.46,0-118.73-53.21-118.73-118.59s53.27-118.59,118.73-118.59,118.74,53.21,118.74,118.59-53.28,118.59-118.74,118.59ZM652.68,465.45c0-11.56-9.38-20.93-20.93-20.93s-20.93,9.37-20.93,20.93l.05,5.37c.19,17.2.71,62.92-6.59,125.57-7.25,62.46-70.53,66.79-71.76,66.87-70.77,6.02-224.91,4.93-310.33-.05-59.67-3.46-71.87-74.83-71.87-94.01-.48-35.53-.33-106.16-.16-181.69l.16-118.81c0-87.85,43.37-109.38,81.35-112.16.52-.07,52.94-7.75,119.63-4.86,11.31.76,21.31-8.49,21.8-20.03.48-11.55-8.49-21.31-20.03-21.8-70.99-2.92-125.29,4.96-125.96,5.12-35.67,2.55-118.65,22.61-118.65,153.73l-.16,118.71c-.16,75.78-.32,146.68.16,182.05,0,42.1,27.2,130.64,111.3,135.54,45.5,2.64,110.28,4.2,171.99,4.2,56.03,0,109.56-1.28,143.73-4.2,34.23-1.96,101.35-26.16,110.37-103.77,7.6-65.32,7.06-112.95,6.87-130.88l-.05-4.91Z" />
									<path class="cls-2" d="M268.07,458.48h111.63c11.56,0,20.93-9.37,20.93-20.94s-9.37-20.93-20.93-20.93h-111.63c-11.55,0-20.93,9.38-20.93,20.93s9.38,20.94,20.93,20.94ZM491.32,528.24h-223.25c-11.55,0-20.93,9.37-20.93,20.93s9.38,20.93,20.93,20.93h223.25c11.55,0,20.93-9.37,20.93-20.93s-9.38-20.93-20.93-20.93ZM622.68,221.27c-3.87-10.89-15.89-16.61-26.71-12.74-28.56,10.12-56.99,41.51-75.17,65.04-7.79-11.21-13.68-17.25-14.69-18.26-8.09-8.13-21.26-8.16-29.46-.04-8.21,8.09-8.26,21.32-.22,29.55.11.12,12.05,12.46,24.07,36.5,3.38,6.74,10.11,11.15,17.63,11.54.38.02.74.03,1.09.03,7.14,0,13.79-3.64,17.66-9.69,15.7-24.65,49.41-66.85,73.04-75.23,10.9-3.87,16.62-15.82,12.75-26.72Z" />
								</svg>
							</div>
							<p class="text-h6 mb-0">
								Equip you with the latest market insights
							</p>
						</v-col>
						<v-col cols="12" sm="4" style="perspective: 120px;">
							<div class="icon--small background--wavy background--wavy--alt--2 rounded-lg mb-4">
								<svg xmlns="http://www.w3.org/2000/svg" class="svg" viewBox="0 0 800 800">
									<path class="cls-2" d="M529.88,173.17c-27.28-40.77-72.82-65.11-121.8-65.11-80.79,0-146.51,65.72-146.51,146.51v78.58l41.86-30.5v-48.07c0-57.71,46.94-104.65,104.65-104.65,35.01,0,67.52,17.38,87.03,46.52,6.43,9.63,19.4,12.17,29.03,5.76,9.62-6.43,12.18-19.43,5.75-29.04ZM408.07,442.96c-11.56,0-20.93,9.38-20.93,20.93v55.81c0,11.55,9.37,20.93,20.93,20.93s20.93-9.38,20.93-20.93v-55.81c0-11.55-9.38-20.93-20.93-20.93Z" />
									<path class="cls-1" d="M561.14,408.26c13.6,24.82,21.34,53.29,21.34,83.53,0,96.17-78.24,174.41-174.4,174.41s-174.41-78.24-174.41-174.41c0-30.23,7.74-58.69,21.33-83.51,29.44-54.32,86.95-91.21,153.08-91.21s123.63,36.88,153.07,91.2M624.33,491.79c0-119.26-97.02-216.29-216.26-216.29-37.95,0-73.61,9.9-104.65,27.14l-41.86,30.5c-42.78,39.54-69.77,95.93-69.77,158.64,0,119.26,97.02,216.27,216.27,216.27s216.26-97.02,216.26-216.27" />
								</svg>
							</div>
							<p class="text-h6 mb-0">
								Unlock opportunities in this dynamic sector
							</p>
						</v-col>
					</v-row>
				</v-container>
				<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-0">
					<span class="font-weight-bold">Our mission is simple:</span> To empower advisers with sector-leading training that not only enhances industry knowledge and professionalism but also supports continuous business growth.
				</p>
				<div class="mt-8 text-xs-center text-md-left mx-n3">
					<v-btn :large="$vuetify.breakpoint.mdAndUp" color="#1b78d8" dark class="mx-3" @click="handleCtaClick({ authRoute: 'academy', route: 'academy-register' })">
						unlock the academy today
					</v-btn>
				</div>
			</div>
		</v-container>

		<v-container>
			<v-divider />
		</v-container>

		<v-container v-if="paths" class="section1 py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<div class="text-center text-sm-left d-flex align-center flex-column mx-auto mb-10" style="max-width: 900px;">
				<p class="text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6">
					Take Control of Your Professional Development
				</p>
				<p class="text-center text-body-1 bypass--line-height--override  text-md-h6 font-weight-regular">
					There are three different ways to unlock your potential with our sector leading training. Choose the path that fits your journey and achieve your professional goals:
				</p>
			</div>
			<div class="d-flex flex-wrap ma-n3">
				<v-card
					v-for="path in paths"
					:key="path.id"
					flat
					class="ma-3 flex-grow-1 flex-shrink-1 d-flex flex-column"
					:style="{ 'flex-basis': $vuetify.breakpoint.lgAndUp ? '30%' : $vuetify.breakpoint.mdOnly ? '40%' : '100%' }"
					style="perspective: 800px;"
				>
					<div class="background--wavy d-flex flex-column align-center py-10 px-6 rounded-lg mb-6" :class="path.class">
						<div>
							<img class="d-block" style="max-width: 180px;" :src="'manager_documents/6aed7079-23e1-4b27-aa51-1440afa17476.svg' | urlize" />
							<p class="text-h3 font-weight-light white--text mt-1 mb-0">
								{{ path.ident }}
							</p>
						</div>
					</div>
					<v-card-title v-if="path.title" class="text-h6 text-md-h5 text-lg-h5 font-weight-bold flex-grow-0 text-center justify-center pt-0 pb-2">
						{{ path.title }}
					</v-card-title>
					<v-card-text class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular flex-grow-1 text-center pt-0 px-6 pb-0" style="line-height: 1.75rem;" v-html="$sanitize(path.copy)" />
				</v-card>
			</div>
			<div class="mt-15 mt-md-12 text-center mx-n3">
				<v-btn :large="$vuetify.breakpoint.mdAndUp" color="#1b78d8" dark class="mx-3" @click="handleCtaClick({ authRoute: 'academy', route: 'academy-register' })">
					unlock the academy today
				</v-btn>
				<v-btn :large="$vuetify.breakpoint.mdAndUp" :href="'manager_documents/036e28bb-01c1-4a34-9127-4e5823cc0243.pdf' | urlize" target="_blank" color="#1b78d8" dark class="mx-3">
					download our brochure
				</v-btn>
				<v-btn :large="$vuetify.breakpoint.mdAndUp" target="_blank" color="#1b78d8" outlined class="mx-3" @click="handleCtaClick({ function: 'arrangeDemo', subject: 'Making the Complex Simple' })">
					request a callback
				</v-btn>
			</div>
		</v-container>

		<v-container>
			<v-divider />
		</v-container>

		<v-container class="section2 py-8" :class="{ 'py-12': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<v-row>
				<v-col order="last" cols="12" md="5">
					<div class="mx-auto mt-6 mt-md-0">
						<div class="background--wavy background--wavy--alt pa-10 rounded-lg">
							<div class="d-flex justify-center">
								<div v-for="item in [5,8,0,0]" :key="item" style="position: relative; perspective: 20px;" class="card--number text-h2 font-weight-black grey--text text--lighten-2 py-4 px-5 grey darken-4 rounded mx-1">
									<span class="d-block">
										{{ item }}
									</span>
								</div>
							</div>
							<p class="text-h5 font-weight-black white--text text-center mt-4 mb-6">
								hours of CPD
							</p>
							<v-row v-for="item in ['Raising Standards', 'Empowering advisers', 'Transforming customer outcomes']" :key="item" class="mx-auto" style="max-width: 400px;">
								<v-col class="shrink align-content-start pb-0">
									<img class="icon--tick d-block mt-1" style="width: 25px;" :src="'manager_documents/7cb51d2f-60cb-4d1b-b8f8-1ec7ea6c080c.svg' | urlize" />
								</v-col>
								<v-col class="grow align-content-center pb-0">
									<p class="text-h5 font-weight-medium white--text mb-0">
										{{ item }}
									</p>
								</v-col>
							</v-row>
						</div>
					</div>
				</v-col>
				<v-col order="first" cols="12" md="7" class="pr-md-15 text-center text-sm-left">
					<p class="text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6">
						Capture and evidence your learning journey
					</p>
					<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-4">
						Our cutting-edge digital learning platform provides practical, hands-on training designed to strengthen skills and support professional growth. In 2024 alone, advisers dedicated over 5,800 hours to CPD through our platform, academy events, and LIBF-accredited courses.
					</p>
					<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-0">
						All your learning can be seamlessly recorded in our dynamic CPD log, ensuring you can evidence your development.
					</p>
					<div class="mt-10 text-xs-center text-md-left mx-n3">
						<v-btn :large="$vuetify.breakpoint.mdAndUp" color="#1b78d8" dark class="mx-3" @click="handleCtaClick({ authRoute: 'academy', route: 'academy-register' })">
							unlock the academy today
						</v-btn>
					</div>
				</v-col>
			</v-row>
		</v-container>

		<v-container>
			<v-divider />
		</v-container>

		<v-container v-if="systems" class="section3 py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<div class="text-sm-left d-flex align-center flex-column mx-auto mb-10">
				<p class="text-center text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6" style="max-width: 700px;">
					Unlock the power of our systems with bespoke webinar training
				</p>
				<p class="text-center text-body-1 bypass--line-height--override  text-md-h6 font-weight-regular">
					Unsure where to begin? Join a live, tailored session with our expert trainers and learn how our award-winning tools can drive your business forward. Each webinar is free to attend and earns you 1 hour of CPD.
				</p>
			</div>
			<div class="d-flex flex-wrap ma-n3">
				<v-card
					v-for="section in systems"
					:key="section.id"
					flat
					class="ma-3 flex-grow-1 flex-shrink-1 d-flex flex-column"
					:style="{ 'flex-basis': $vuetify.breakpoint.lgAndUp ? '30%' : $vuetify.breakpoint.mdOnly ? '40%' : '100%' }"
					style="perspective: 800px;"
				>
					<div v-if="section.id === 'sourcing'" class="background--wavy pa-6 rounded-lg mb-6 align-content-center" style="min-height: 200px;">
						<img class="d-block mx-auto" style="max-width: 250px;" :src="'manager_documents/6c9f401f-e7a0-4366-84b8-32d13518475c.svg' | urlize" />
					</div>
					<div v-if="section.id === 'writeroute'" class="background--wavy--alt background--wavy pa-6 rounded-lg mb-6 align-content-center" style="min-height: 200px;">
						<img class="d-block mx-auto" style="max-width: 250px;" :src="'manager_documents/f1f2270a-1411-467f-9414-c78a2ef7318b.svg' | urlize" />
					</div>
					<div v-if="section.id === 'navigator'" class="background--wavy--alt--2 background--wavy pa-6 rounded-lg mb-6 align-content-center" style="min-height: 200px;">
						<img class="d-block mx-auto" style="max-width: 250px;" :src="'manager_documents/6afd2984-d064-49c9-a06b-0e681ff1a6c2.svg' | urlize" />
					</div>

					<v-card-title v-if="section.title" class="text-h6 text-md-h5 text-lg-h5 font-weight-bold flex-grow-0 text-center justify-center pt-0 pb-2">
						{{ section.title }}
					</v-card-title>

					<v-card-text class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular flex-grow-1 text-center pt-0 px-6 pb-0" style="line-height: 1.75rem;" v-html="$sanitize(section.copy)" />
				</v-card>
			</div>
			<div class="mt-15 mt-md-12 text-center mx-n3">
				<v-btn :large="$vuetify.breakpoint.mdAndUp" to="/events?category=Training" color="#1b78d8" dark class="mx-3">
					select your webinar here
				</v-btn>
			</div>
		</v-container>

		<v-container>
			<v-divider />
		</v-container>

		<v-container id="trust" class="section4 py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<div class="text-sm-left d-flex align-center flex-column mx-auto mb-3">
				<p class="text-center text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6" style="max-width: 700px;">
					Partnered with Excellence
				</p>
				<p class="text-center text-body-1 bypass--line-height--override text-md-h6 font-weight-regular">
					In partnership with industry leaders such as the LIBF and the Equity Release Council, our educational programmes set the benchmark for quality—equipping advisers with the skills, knowledge and compliance standards needed to thrive. Together, we’re raising industry standards and supporting long-term professional and business growth.
					<strong>Explore below to see how we're enhancing education across the later life lending market.</strong>
				</p>
			</div>
			<v-row>
				<v-col cols="12" md="6" class="d-flex flex-column justify-space-between">
					<div class="mb-6 px-4 d-none">
						<p class="text-center text-body-1 text-md-h6 text-lg-h5 font-weight-bold mb-3">
							Factfind Learning Programme
						</p>
						<p class="text-center text-body-2 text-md-body-1 font-weight-regular mb-0">
							This integrates a comprehensive review of WriteRoute with an LIBF-accredited curriculum, all underpinned by Equity Release Council endorsed best practices.
						</p>
					</div>
					<div style="padding:56.25% 0 0 0;position:relative;">
						<iframe src="https://player.vimeo.com/video/1070257492" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" style="position:absolute;top:0;left:0;width:100%;height:100%;" />
					</div>
				</v-col>
				<v-col cols="12" md="6" class="d-flex flex-column justify-space-between">
					<div class="mb-6 px-4 d-none">
						<p class="text-center text-body-1 text-md-h6 text-lg-h5 font-weight-bold mb-3">
							Factfind Learning Programme
						</p>
						<p class="text-center text-body-2 text-md-body-1 font-weight-regular mb-0">
							Recognised for outstanding training quality, Air Academy is one of only six global LIBF Trusted Partners.
						</p>
					</div>
					<div style="padding:56.25% 0 0 0;position:relative;">
						<iframe src="https://player.vimeo.com/video/1066978848" frameborder="0" allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" style="position:absolute;top:0;left:0;width:100%;height:100%;" />
					</div>
				</v-col>
			</v-row>
			<div class="mt-15 mt-md-12 text-center mx-n3">
				<v-btn :large="$vuetify.breakpoint.mdAndUp" color="#1b78d8" dark class="mx-3" @click="handleCtaClick({ authRoute: 'academy', route: 'academy-register' })">
					Access the FF Learning Programme Now
				</v-btn>
				<v-btn :large="$vuetify.breakpoint.mdAndUp" to="/events?category=Training" color="#1b78d8" dark outlined class="mx-3">
					Learn More about WriteRoute
				</v-btn>
			</div>
		</v-container>

		<v-container>
			<v-divider />
		</v-container>

		<v-container v-if="systems" class="section5 py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }" style="max-width: 1050px;">
			<div class="text-center d-flex align-center flex-column px-md-10">
				<p class="text-center text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-6" style="max-width: 700px;">
					What Our Members Say
				</p>
				<v-icon color="secondary" size="40" class="mb-4">
					mdi-format-quote-open
				</v-icon>
				<v-carousel height="auto" cycle interval="8000" hide-delimiter-background hide-delimiters :show-arrows="false">
					<v-carousel-item v-for="testimonial in testimonials" :key="testimonial.name">
						<p class="text-h6 mb-0">
							{{ testimonial.copy }}
						</p>
						<p v-if="testimonial.name" class="text-h5 font-weight-bold mt-6 mb-0">
							{{ testimonial.name }}
						</p>
						<p v-if="testimonial.position" class="text-body-1 mt-1 mb-0">
							{{ testimonial.position }}
						</p>
					</v-carousel-item>
				</v-carousel>
			</div>
		</v-container>

		<banner class="mt-10" :auth="auth" @handle-cta-click="handleCtaClick" />

		<div id="contact" ref="contact" class="header--waves scroll-margin--fixed-header-offset">
			<!--Content before waves-->
			<div class="inner-header flex">
				<v-container class="py-8" :class="{ 'py-15': $vuetify.breakpoint.mdAndUp }">
					<div class="text-md-center d-flex align-center flex-column">
						<div class="grey lighten-4 pa-8 rounded elevation-6" style="max-width: 700px;">
							<v-fade-transition>
								<div v-if="success">
									<p class="text-h6 text-md-h5 text-lg-h4 font-weight-bold mb-4 mx-auto" style="max-width: 500px;">
										Thank you for reaching out!
									</p>
									<p class="text-body-1 bypass--line-height--override text-md-h6 font-weight-regular mb-6 mx-auto" style="max-width: 500px;">
										Your email has been sent. We'll get back to you shortly. In the meantime, check out <a class="ga--trigger ga--campaign-click ga--events" @click="$router.push('/events')">upcoming events</a> or read more <a class="ga--trigger ga--campaign-click ga--academy" @click="$router.push('/air-academy')">about us</a>.
									</p>
									<v-icon color="success" size="60">
										mdi-check-circle
									</v-icon>
								</div>
							</v-fade-transition>
							<template v-if="!success">
								<p class="text-center text-h6 font-weight-regular mb-6 mx-6" v-html="$sanitize(form.copy)" />
								<validation-observer ref="observer">
									<v-form @submit.prevent="submit">
										<v-row>
											<v-col cols="12" md="6">
												<validation-provider v-slot="{ errors }" name="First Name" rules="required">
													<v-text-field v-model="firstName" :disabled="disabled" :error-messages="errors" label="First Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12" md="6">
												<validation-provider v-slot="{ errors }" name="Last Name" rules="required">
													<v-text-field v-model="lastName" :disabled="disabled" :error-messages="errors" label="Last Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Firm Name" rules="required">
													<v-text-field v-model="firmName" :disabled="disabled" :error-messages="errors" label="Firm Name" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="FCA Number" rules="required|numeric|min:6|max:7">
													<v-text-field v-model="fcaNumber" :disabled="disabled" :error-messages="errors" label="FCA Number" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Email" rules="required|email">
													<v-text-field v-model="email" :disabled="disabled" :error-messages="errors" label="Email" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<validation-provider v-slot="{ errors }" name="Phone" rules="required|numeric">
													<v-text-field v-model="phone" :disabled="disabled" :error-messages="errors" label="Phone" :hide-details="!errors?.length" filled />
												</validation-provider>
											</v-col>
											<v-col cols="12">
												<v-btn class="ga--trigger ga--campaign-click ga--form-submit white--text" color="#1b78d8" :disabled="disabled" :loading="loading" type="submit" large block>
													Submit for a call back
												</v-btn>
											</v-col>
										</v-row>
									</v-form>
								</validation-observer>
							</template>
						</div>
					</div>
				</v-container>
			</div>

			<!--Waves Container-->
			<div>
				<svg
					class="waves"
					xmlns="http://www.w3.org/2000/svg"
					xmlns:xlink="http://www.w3.org/1999/xlink"
					viewBox="0 24 150 28"
					preserveAspectRatio="none"
					shape-rendering="auto"
				>
					<defs>
						<path id="gentle-wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
					</defs>
					<g class="parallax">
						<use xlink:href="#gentle-wave" x="48" y="0" fill="rgba(255,255,255,0.7" />
						<use xlink:href="#gentle-wave" x="48" y="3" fill="rgba(255,255,255,0.5)" />
						<use xlink:href="#gentle-wave" x="48" y="5" fill="rgba(255,255,255,0.3)" />
						<use xlink:href="#gentle-wave" x="48" y="7" fill="#fff" />
					</g>
				</svg>
			</div>
			<!--Waves end-->
		</div>
		<div class="body--waves d-flex" />

		<v-container class="pb-5">
			<p class="text-caption grey--text text--darken-2 mb-0">
				* from January 2024 to January 2025, a sample group of 307 professional status advisers recorded 1,817 completions, versus a control group of 307 non-professional status advisers from similar tiers, who completed 357 completions
			</p>
		</v-container>
	</div>
</template>

<script>
	import { mapActions } from 'vuex';
	import { gsap } from 'gsap';
    import ScrollTrigger from "gsap/ScrollTrigger";
	import Banner from '@/component/app/air-later-life/academy/banner';
	import { ValidationObserver, ValidationProvider } from 'vee-validate';
	import { EventBus, ElementTools } from '@/utils';
	import { orderBy } from 'lodash';

	export default {
		name: 'app-academy',

		components: {
            Banner,
            ValidationObserver,
            ValidationProvider
		},

		data() {
			return {
				utm_id: null,
				auth: null,
                timelines: [],
                form: {
                    "copy": "Contact a member of the team if you'd like to <strong>know more about Air Academy</strong> or for support using any of our safer tracks"
                },
                campaign: 'Academy Page',
                subject: null,
                firstName: '',
				lastName: '',
				firmName: '',
				fcaNumber: '',
				email: '',
				phone: '',
				message: '',
				loading: false,
				success: false,
				disabled: false,
                paths: [
                    {
                        "ident": "Essentials",
                        "title": "Air Academy Essentials",
                        "copy": '<p class="mb-0">This offers Air members free access to training, CPD resources, live event booking, an introduction to our LIBF-accredited content, and a dynamic CPD log.</p>',
                        "show": true,
                        "ctas": [
                            {
                                "copy": "Arrange a demo",
                                "color": "#1b78d8",
                                "function": "arrangeDemo"
                            },
                            {
                                "copy": "Let's chat!",
                                "color": "secondary",
                                "function": "openChat"
                            }
                        ],
                        "poster": "/cms/AIR+Campaign+Video+Screen_001a.jpg"
                    },
                    {
                        "ident": "Premium",
                        "title": "Air Academy Premium",
                        "copy": `<p class="mb-0">This monthly subscription unlocks Air's LIBF-accredited Adviser Accreditation Programme, granting a digital badge of excellence, ambassador offers, and enhanced procuration fees upon completion.</p>`,
                        "show": true,
                        "ctas": [
                            {
                                "copy": "Arrange a demo",
                                "color": "#1b78d8",
                                "function": "arrangeDemo"
                            },
                            {
                                "copy": "Let's chat!",
                                "color": "secondary",
                                "function": "openChat"
                            }
                        ],
                        "poster": "/cms/AIR+Campaign+Video+Screen_001a.jpg",
						"class": "background--wavy--alt"
                    },
                    {
                        "ident": "Accelerator",
                        "title": "Air Academy Accelerator",
                        "copy": `<p class="mb-0">Air Academy's LIBF-accredited Later Life Lending Accelerator blends practical learning with customer journey insights, focusing on good outcomes. Available for a one-off payment.</p>`,
                        "show": true,
                        "ctas": [
                            {
                                "copy": "Arrange a demo",
                                "color": "#1b78d8",
                                "function": "arrangeDemo"
                            },
                            {
                                "copy": "Let's chat!",
                                "color": "secondary",
                                "function": "openChat"
                            }
                        ],
                        "poster": "/cms/AIR+Campaign+Video+Screen_001a.jpg",
						"class": "background--wavy--alt--2"
                    }
                ],
				systems: [
                    {
                        "id": "sourcing",
                        "title": "Air Sourcing",
                        "copy": '<p class="mb-0">Enhanced technology platform supporting advisers in recommending the most suitable product.</p>',
                        "show": true,
                        "ctas": [
                            {
                                "copy": "Arrange a demo",
                                "color": "#1b78d8",
                                "function": "arrangeDemo"
                            },
                            {
                                "copy": "Let's chat!",
                                "color": "secondary",
                                "function": "openChat"
                            }
                        ],
                        "poster": "/cms/AIR+Campaign+Video+Screen_001a.jpg"
                    },
                    {
                        "id": "writeroute",
                        "title": "WriteRoute",
                        "copy": `<p class="mb-0">Streamlined fact-find capturing and evidencing your client's short, medium and long-term needs.</p>`,
                        "show": true,
                        "ctas": [
                            {
                                "copy": "Arrange a demo",
                                "color": "#1b78d8",
                                "function": "arrangeDemo"
                            },
                            {
                                "copy": "Let's chat!",
                                "color": "secondary",
                                "function": "openChat"
                            }
                        ],
                        "poster": "/cms/AIR+Campaign+Video+Screen_001a.jpg"
                    },
                    {
                        "id": "navigator",
                        "title": "Air Navigator",
                        "copy": `<p class="mb-0">Identify the most suitable options for your clients from a wide range of product types.</p>`,
                        "show": true,
                        "ctas": [
                            {
                                "copy": "Arrange a demo",
                                "color": "#1b78d8",
                                "function": "arrangeDemo"
                            },
                            {
                                "copy": "Let's chat!",
                                "color": "secondary",
                                "function": "openChat"
                            }
                        ],
                        "poster": "/cms/AIR+Campaign+Video+Screen_001a.jpg"
                    }
                ],
				testimonials: [
					{
						copy: `The knowledge I gained of the Later Life Lending products, processes and tools available to support the journey has given me confidence in my abilities to advise correctly.`,
						name: `Donna Murray`
					},
					{
						copy: `I have really enjoyed completing the modules as they have helped me gain a great deal of knowledge of the products available and compliance required which has given me confidence in dealing with later life lending.`,
						name: `Collette Campbell`
					},
					{
						copy: `I would recommend the Air Later Life Lending Adviser Accelerator to anyone who is considering advising on Later Life Lending`,
						name: `Graham Pike`
					},
					{
						copy: `Honestly, I think that everyone in Later Life Lending should be going through this course`,
						name: `Kris Buda`
					}
				]
			};
		},

		computed: {
			banners() {
				let arr = [
					{
						id: 1,
						title: '3 times as many completions for Air Academy Professionals*',
						titleStyle: { 'line-height': this.$vuetify.breakpoint.lgAndUp ? '4rem' : 'auto' },
						image: 'manager_documents/c710e590-fb3a-45f3-84be-5ae0e8d7d265.png',
						maxWidth: '750px'
					},
					{
						id: 2,
						title: 'Equity Release Council Learning Programme',
						copy: `Available exclusively to all Air members.`,
						cta: {
							text: 'click here to learn more',
							to: '/comprehensive-conversations',
							color: 'white',
							click: {
								function: 'goToTrust'
							}
						},
						image: 'manager_documents/4187a0df-1f17-4bbe-8d28-2669895a0833.jpg',
						maxWidth: '700px'
					}
				];

				// Change order when requested
				if (this.utm_id === 'erc') arr = orderBy(
					arr,
					(x) => x.id,
					['desc']
				);

				return arr;
			}
		},

		async created() {
			this.auth = window.localStorage.getItem('air.authorization');
			this.utm_id = this.$route.query.utm_id;
		},

		mounted() {
            this.scrollTriggerInit();
        },

		methods: {
			...mapActions('ContactCampaign', ['submitContactCampaign']),

			scrollTriggerInit() {
                let mm = gsap.matchMedia();
                gsap.registerPlugin(ScrollTrigger);

                mm.add("(min-width: 1200px)", () => {
					let t0 = gsap.timeline({
                        scrollTrigger: {
                            trigger: '.section0',
                            start: 'top+=500 bottom',
							end: 'top',
							scrub: true
                        },
                        defaults: { ease: 'back.out(1.7)' }
                    });
                    t0.from('.section0 .background--wavy', { stagger: 0.25, duration: 0.5, opacity: 0, scale:0.9, transform: 'rotateX(-20deg)' });
                    this.timelines.push(t0);

					let t2 = gsap.timeline({
                        scrollTrigger: {
                            trigger: '.section1',
                            start: 'top+=400 bottom',
							end: '+=300',
							scrub: true
                        },
                        defaults: { ease: 'none'}
                    });
                    t2.from('.section1 .background--wavy', { stagger: 0.1, duration: 0.25, opacity: 0, transform: 'rotateX(10deg)' });
                    this.timelines.push(t2);

                    let tl = gsap.timeline({
                        scrollTrigger: {
                            trigger: '.section2',
                            start: 'top+=200 bottom',
							end: 'top-=100',
							scrub: true
                        },
                        defaults: { ease: 'back.out(1.7)' }
                    });
                    tl.from('.section2 .card--number span', { stagger: 0.1, duration: 0.25, scale: 0.75, opacity: 0, y: 100, transform: 'rotateX(-30deg)' });
                    tl.from('.section2 .icon--tick', { stagger: 0.1, duration: 0.33, scale: 0, opacity: 0, y: 10, transform: 'rotateY(90deg)' }, "<+=0.2");
                    this.timelines.push(tl);

					let t3 = gsap.timeline({
                        scrollTrigger: {
                            trigger: '.section3',
                            start: 'top+=400 bottom',
							end: '+=300',
							scrub: true
                        },
                        defaults: { ease: 'none'}
                    });
                    t3.from('.section3 .background--wavy', { stagger: 0.1, duration: 0.25, opacity: 0, transform: 'rotateX(10deg)' });
                    this.timelines.push(t3);

					let t4 = gsap.timeline({
                        scrollTrigger: {
                            trigger: '.banner--animation',
                            start: 'top bottom',
                            end: 'bottom 50%+=100px',
                            scrub: true
                        },
                        defaults: { ease: 'none'}
                    });

                    // animate the container one way...
                    t4.from('.banner--animation #bottom_cube', { y: -25 });
                    t4.from('.banner--animation #top_cube', { y: 25 }, "<");
                    this.timelines.push(t4);
                });
            },

            handleCtaClick(cta) {
				if (cta.function) {
					let fnctn = cta.function;
					if (fnctn === 'arrangeDemo') this.scrollToAnchorPoint('contact', cta.subject || null);
					if (fnctn === 'goToTrust') this.scrollToAnchorPoint('trust', null);
					if (fnctn === 'openChat') EventBus.$emit('open-chat');
				} else if (cta.route) {
					let route = cta.route;
					if (this.auth && cta.authRoute) {
						route = cta.authRoute;
						this.$router.push(route);
					} else {
						this.$router.push(route);
					}
				}
			},

            scrollToAnchorPoint(refName, subject = null) {
				if (subject) this.subject = subject;
                const id = refName;
                const yOffset = -140;
                const element = document.getElementById(id);
                const y = element.getBoundingClientRect().top + window.scrollY + yOffset;

                window.scrollTo({top: y, behavior: 'smooth'});
            },

			submit() {
				this.$refs.observer.validate().then(async(valid) => {
					if (!valid) return false;

					this.success = false;
					this.loading = true;

					this.submitContactCampaign({
						firstName: this.firstName,
						lastName: this.lastName,
						firmName: this.firmName,
						fcaNumber: this.fcaNumber,
						email: this.email,
						phone: this.phone,
						campaign: this.campaign,
						subject: this.subject
					})
						.then(() => {
							this.success = true;
							this.resetForm();
						})
						.catch(() => ElementTools.fireNotification(this.$el, 'error', 'An error occurred. Please try again later.'))
						.finally(() => {
							this.subject = null;
							this.loading = false;
						});
				});
			},

			resetForm() {
				this.subject = null;
				this.firstName = '';
				this.lastName = '';
				this.firmName = '';
				this.fcaNumber = '';
				this.email = '';
				this.phone = '';
				this.disabled = true;
				this.$refs.observer.reset();
			}
        }
	};
</script>

<style lang="scss" scoped>
    @import '~vuetify/src/styles/settings/_variables';

	.banner--copy {
		line-height: 55px;

		&--small {
			line-height: 40px;
		}
	}

	.card--number {
		overflow: hidden;
		box-shadow: 0px 6px 8px 2px rgba(0, 0, 0, 0.25);

		&::after {
			content: "";
			position: absolute;
			width: 100%;
			height: 100%;
			background: linear-gradient(rgba(0,0,0,0) 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.75));
			top: 0;
			left: 0;
		}
	}

	.icon--small {
		width: 150px;
		padding: 20px;
		margin: 0 auto;
		//border: 3px solid #61e37a;
		//box-shadow: 0px 6px 8px 2px rgba(0, 0, 0, 0.25);
	}

	.background--wavy {
		background-color: #3f3195;
		background-position: 50% 50%;
		background-size: cover;
		background-image: url($VUE_APP_S3_AIR_ASSETS_URL+'/manager_documents/7aed7769-4814-4c94-bc82-6db969ef5dda.png');

		&--alt {
			background-image: url($VUE_APP_S3_AIR_ASSETS_URL+'/manager_documents/4f349c68-854f-4f1d-ad40-afe568de8fed.png');
		}

		&--alt--2 {
			background-image: url($VUE_APP_S3_AIR_ASSETS_URL+'/manager_documents/73959188-6cd2-46b4-8091-5241e24ab0a7.png');
		}

		svg {
			display: block;
		}
	}

    .svg {
        .gradient {
            fill: url(#gradient) #3f3195;
        }

        .gradient--alt {
            fill: url(#gradient--alt) #372387;
        }

        .gradient--line {
            fill: url(#gradient--line) #372387;
        }

		.cls-1 {
			fill: #f9f8f4;
		}

		.cls-2 {
			fill: #61e37a;
		}
    }

	.wrap {
		background-position: 50% 50%;
		background-size: cover;
	}

    .hero {
        position: relative;
        overflow: hidden;
        z-index: 1;
        height: 300px;
        border-bottom: 20px solid #1b78d8;
        background-position: 50% 50%;
        background-repeat: no-repeat;
        background-size: cover;
		background-image: url($VUE_APP_S3_AIR_ASSETS_URL + '/manager_documents/c710e590-fb3a-45f3-84be-5ae0e8d7d265.png');
    }

    @media #{map-get($display-breakpoints, 'md-and-up')} {
        .hero {
            height: 500px;
        }
    }

    .header--waves {
        background-position: 50% 50%;
        background-size: cover;
        background-attachment: fixed;
        background-image: url($VUE_APP_S3_AIR_ASSETS_URL+'/manager_documents/d0bb7b46-b858-45ac-8e11-15f9c7883eb7.png');
        position:relative;
    }

    .inner-header {
        width:100%;
        margin: 0;
        padding: 0;
    }

    .waves {
        position:relative;
        width: 100%;
        height:50px;
        margin-bottom:-7px; /*Fix for safari gap*/
        min-height:50px;
        max-height:50px;
    }

    .body--waves {
        position:relative;
        height:50px;
        text-align:center;
        background-color: white;
    }

	    /* Animation */
		.parallax > use {
        animation: move-forever 25s cubic-bezier(.55,.5,.45,.5)     infinite;
    }
    .parallax > use:nth-child(1) {
        animation-delay: -2s;
        animation-duration: 7s;
    }
    .parallax > use:nth-child(2) {
        animation-delay: -3s;
        animation-duration: 10s;
    }
    .parallax > use:nth-child(3) {
        animation-delay: -4s;
        animation-duration: 13s;
    }
    .parallax > use:nth-child(4) {
        animation-delay: -5s;
        animation-duration: 20s;
    }
    @keyframes move-forever {
        0% {
        transform: translate3d(-90px,0,0);
        }
        100% {
            transform: translate3d(85px,0,0);
        }
    }
    /*Shrinking for mobile*/
    @media (max-width: 768px) {
        .waves {
            height:40px;
            min-height:40px;
        }
        .body--waves {
            height:30vh;
        }
    }
</style>
